import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useUIStore } from '../../lib/uiStore';
import { supabase } from '../../../lib/supabase';
import {
  Search, CircuitBoard, FileText, Layers, GitBranch, ClipboardList, History,
  Package, Truck, DollarSign, Shield, Users, Settings, ArrowRight,
  Plus, HelpCircle, Sun, Moon, Command,
} from 'lucide-react';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  group: 'navigation' | 'action' | 'search' | 'recent';
  kbd?: string;
}

const NAV_ITEMS: Omit<CommandItem, 'action'>[] = [
  { id: 'nav-dashboard', label: 'Dashboard', icon: <Command className="h-4 w-4" />, group: 'navigation', kbd: 'G D' },
  { id: 'nav-products', label: 'Circuit Designs', description: 'Product portfolio', icon: <CircuitBoard className="h-4 w-4" />, group: 'navigation', kbd: 'G P' },
  { id: 'nav-docs', label: 'Schematics & Docs', description: 'Document management', icon: <FileText className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-bom', label: 'Bill of Materials', description: 'Assembly structures', icon: <Layers className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-changes', label: 'Engineering Changes', description: 'Change requests', icon: <GitBranch className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-orders', label: 'Change Orders', description: 'ECO implementation', icon: <ClipboardList className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-revisions', label: 'Design Revisions', icon: <History className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-inventory', label: 'Component Vault', description: 'Inventory management', icon: <Package className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-supply', label: 'Supplier Network', description: 'Purchase orders', icon: <Truck className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-finance', label: 'Cost Engineering', icon: <DollarSign className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-admin', label: 'Admin Dashboard', icon: <Shield className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-users', label: 'User Management', icon: <Users className="h-4 w-4" />, group: 'navigation' },
  { id: 'nav-settings', label: 'Settings', icon: <Settings className="h-4 w-4" />, group: 'navigation' },
];

const NAV_PATHS: Record<string, string> = {
  'nav-dashboard': '/', 'nav-products': '/plm/products', 'nav-docs': '/pdm/documents',
  'nav-bom': '/pdm/bom', 'nav-changes': '/ecm/changes', 'nav-orders': '/ecm/orders',
  'nav-revisions': '/ecm/revisions', 'nav-inventory': '/erp/inventory',
  'nav-supply': '/erp/supply-chain', 'nav-finance': '/erp/finance',
  'nav-admin': '/admin', 'nav-users': '/admin/users', 'nav-settings': '/settings',
};

export function CommandPalette() {
  const navigate = useNavigate();
  const open = useUIStore((s) => s.commandPaletteOpen);
  const close = useUIStore((s) => s.closeCommandPalette);
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<CommandItem[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Build items list
  const actionItems: CommandItem[] = [
    { id: 'action-new-product', label: 'New Product', description: 'Create a circuit design', icon: <Plus className="h-4 w-4" />, group: 'action', action: () => { navigate('/plm/products'); close(); } },
    { id: 'action-new-cr', label: 'New Change Request', description: 'Submit an ECR', icon: <Plus className="h-4 w-4" />, group: 'action', action: () => { navigate('/ecm/changes'); close(); } },
    { id: 'action-new-bom', label: 'New BOM', description: 'Create bill of materials', icon: <Plus className="h-4 w-4" />, group: 'action', action: () => { navigate('/pdm/bom'); close(); } },
    { id: 'action-new-po', label: 'New Purchase Order', icon: <Plus className="h-4 w-4" />, group: 'action', action: () => { navigate('/erp/supply-chain'); close(); } },
    { id: 'action-theme', label: `Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`, icon: theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />, group: 'action', action: () => { toggleTheme(); close(); } },
    { id: 'action-help', label: 'Help & Shortcuts', icon: <HelpCircle className="h-4 w-4" />, group: 'action', kbd: '?', action: () => { close(); } },
  ];

  const navItems: CommandItem[] = NAV_ITEMS.map((item) => ({
    ...item,
    action: () => { navigate(NAV_PATHS[item.id]); close(); },
  }));

  const allItems = [...actionItems, ...navItems, ...searchResults];

  // Filter by query
  const filtered = query.trim()
    ? allItems.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        item.description?.toLowerCase().includes(query.toLowerCase())
      )
    : allItems;

  // Group items
  const grouped = {
    search: filtered.filter((i) => i.group === 'search'),
    action: filtered.filter((i) => i.group === 'action'),
    navigation: filtered.filter((i) => i.group === 'navigation'),
  };
  const flatFiltered = [...grouped.search, ...grouped.action, ...grouped.navigation];

  // Search Supabase when query changes
  useEffect(() => {
    if (!query.trim() || query.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const results: CommandItem[] = [];
        const [products, docs, crs] = await Promise.all([
          supabase.from('products').select('id, name, part_number').ilike('name', `%${query}%`).limit(5),
          supabase.from('documents').select('id, name, doc_number').ilike('name', `%${query}%`).limit(5),
          supabase.from('change_requests').select('id, title, cr_number').ilike('title', `%${query}%`).limit(5),
        ]);
        products.data?.forEach((p) => results.push({
          id: `search-product-${p.id}`, label: p.name, description: p.part_number,
          icon: <CircuitBoard className="h-4 w-4 text-primary-500" />, group: 'search',
          action: () => { navigate('/plm/products'); close(); },
        }));
        docs.data?.forEach((d) => results.push({
          id: `search-doc-${d.id}`, label: d.name, description: d.doc_number,
          icon: <FileText className="h-4 w-4 text-info-500" />, group: 'search',
          action: () => { navigate('/pdm/documents'); close(); },
        }));
        crs.data?.forEach((c) => results.push({
          id: `search-cr-${c.id}`, label: c.title, description: c.cr_number,
          icon: <GitBranch className="h-4 w-4 text-warning-500" />, group: 'search',
          action: () => { navigate('/ecm/changes'); close(); },
        }));
        setSearchResults(results);
      } catch { /* silently fail */ }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, navigate, close]);

  // Reset on open
  useEffect(() => {
    if (open) { setQuery(''); setActiveIndex(0); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, flatFiltered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && flatFiltered[activeIndex]) { flatFiltered[activeIndex].action(); }
    else if (e.key === 'Escape') { close(); }
  }, [flatFiltered, activeIndex, close]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) close(); else useUIStore.getState().openCommandPalette();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, close]);

  if (!open) return null;

  let itemIndex = -1;

  const renderGroup = (label: string, items: CommandItem[]) => {
    if (items.length === 0) return null;
    return (
      <div>
        <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-content-muted">{label}</p>
        {items.map((item) => {
          itemIndex++;
          const idx = itemIndex;
          return (
            <button
              key={item.id}
              data-index={idx}
              onClick={item.action}
              onMouseEnter={() => setActiveIndex(idx)}
              className={cn(
                'w-full flex items-center gap-3 px-3.5 py-3 text-left transition-all rounded-[18px] mx-1',
                idx === activeIndex ? 'bg-primary-500/10 text-primary-500 shadow-subtle' : 'text-content-secondary hover:text-content hover:bg-surface-overlay/70'
              )}
            >
              <span className="flex-shrink-0 opacity-70">{item.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold tracking-[-0.02em] truncate">{item.label}</p>
                {item.description && <p className="text-xs text-content-muted truncate">{item.description}</p>}
              </div>
              {item.kbd && <kbd className="text-[10px] font-mono text-content-muted bg-surface-overlay px-2 py-1 rounded-full border border-border-muted">{item.kbd}</kbd>}
              <ArrowRight className="h-3 w-3 opacity-30 flex-shrink-0" />
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Command palette">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={close} />
      <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-lg animate-slide-up">
        <div className="bg-surface-raised/95 backdrop-blur-2xl border border-border rounded-[30px] shadow-modal overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-surface-overlay/45">
            <Search className={cn('h-5 w-5 flex-shrink-0', searching ? 'text-primary-500 animate-spin-slow' : 'text-content-muted')} />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
              onKeyDown={handleKeyDown}
              placeholder="Search or type a command..."
              className="flex-1 bg-transparent text-content placeholder:text-content-muted text-sm font-medium tracking-[-0.02em] outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            <kbd className="text-[10px] font-mono text-content-muted bg-surface-overlay px-2 py-1 rounded-full border border-border-muted">ESC</kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[400px] overflow-y-auto py-2 px-1 space-y-1">
            {flatFiltered.length === 0 ? (
              <div className="py-8 text-center text-content-muted text-sm">
                {query ? 'No results found' : 'Start typing to search...'}
              </div>
            ) : (
              <>
                {renderGroup('Search Results', grouped.search)}
                {renderGroup('Actions', grouped.action)}
                {renderGroup('Navigation', grouped.navigation)}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surface-overlay/45 text-[10px] text-content-muted">
            <div className="flex items-center gap-3">
              <span><kbd className="font-mono bg-surface-overlay px-1.5 py-0.5 rounded-full">↑↓</kbd> navigate</span>
              <span><kbd className="font-mono bg-surface-overlay px-1.5 py-0.5 rounded-full">↵</kbd> select</span>
              <span><kbd className="font-mono bg-surface-overlay px-1.5 py-0.5 rounded-full">esc</kbd> close</span>
            </div>
            <span>ElectroPLM</span>
          </div>
        </div>
      </div>
    </div>
  );
}
